import { useTranslation } from "react-i18next";

import { Building2, ChevronsUpDown } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { getTenantId } from "@/global/TenantGuard";
import { useAllTenantsQuery } from "@/global/TenantGuard/api";
import { setLocalStorageTenantId } from "@/global/TenantGuard/localStorage";
import { isDevelopment } from "@/utils/helpers";

export function TenantSwitcher() {
  const { t } = useTranslation();
  const currentTenantId = getTenantId();
  const allTenants = useAllTenantsQuery();

  if (!isDevelopment()) return null;

  const current = allTenants.data?.find(
    (t) => t.identifier === currentTenantId,
  );

  const handleChange = (identifier: string) => {
    setLocalStorageTenantId(identifier);
    window.location.href = "/";
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-md">
                <Building2 className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {current?.name ?? t("components.tenantSwitcher.selectTenant")}
                </span>
                <span className="truncate text-xs">
                  {t("components.tenantSwitcher.devOnly")}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
            align="start"
            side="right"
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              {t("components.tenantSwitcher.tenants")}
            </DropdownMenuLabel>
            {allTenants.data?.map((tenant) => (
              <DropdownMenuItem
                key={tenant.identifier}
                onSelect={() => handleChange(tenant.identifier)}
              >
                {tenant.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
